import { useState } from 'react'
import { useNotify, useStore, useTranslate } from 'react-admin'
import { deadlineFetch } from './customEndpointFetch'

export type GlucoseUnit = 'mg/dL' | 'mmol/L'

/**
 * Read + change the glucose display unit. Single write path shared by the
 * profile dialog and the chart-header unit toggle so the persistence rules
 * can't drift apart again (the repeated "units don't switch" reports all
 * trace back to writers that updated one store but not the others).
 *
 * Changing the unit:
 *   1. updates the react-admin store optimistically — every mounted chart
 *      subscribed via useStore('glucoseUnit') re-renders immediately,
 *   2. persists to the backend (PATCH /api/users/:id settings.glucoseUnit)
 *      so the preference follows the ACCOUNT, not the browser — login
 *      re-seeds localStorage from this value (see authProvider.login),
 *   3. invalidates the cached /me identity so checkAuth() can't re-derive
 *      the old unit from a warm 5-minute identity cache,
 *   4. reverts the store and notifies on failure.
 *
 * Returns the updated user doc on success (callers holding local user state,
 * e.g. the profile dialog, can sync from it), or null on failure/no-op.
 */
export const useGlucoseUnitPreference = () => {
  const [glucoseUnit, setGlucoseUnit] = useStore<GlucoseUnit>('glucoseUnit', 'mmol/L')
  const [saving, setSaving] = useState(false)
  const notify = useNotify()
  const translate = useTranslate()

  const changeGlucoseUnit = async (
    newUnit: GlucoseUnit,
    knownUser?: { id: number | string; settings?: Record<string, unknown> },
  ): Promise<any | null> => {
    if (newUnit === glucoseUnit) return null
    const previousUnit = glucoseUnit

    // Optimistic: flip every subscribed chart right away
    setGlucoseUnit(newUnit)
    setSaving(true)
    try {
      const token = sessionStorage.getItem('token')
      const authHeaders: Record<string, string> = { Accept: 'application/json' }
      if (token) {
        authHeaders.Authorization = `Bearer ${token}`
      }

      // The PATCH needs the user id and current settings; callers that
      // already hold the user (profile dialog) pass it to skip the /me trip.
      let me = knownUser
      if (!me?.id) {
        const meResponse = await deadlineFetch('/api/users/me', { headers: authHeaders })
        if (!meResponse.ok) {
          throw new Error(`/me failed: ${meResponse.status}`)
        }
        const meData = await meResponse.json()
        me = meData?.user || meData
      }
      if (!me?.id) {
        throw new Error('No authenticated user')
      }

      const response = await deadlineFetch(`/api/users/${me.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settings: {
            ...(me.settings || {}),
            glucoseUnit: newUnit,
          },
        }),
      })
      if (!response.ok) {
        throw new Error(`unit save failed: ${response.status}`)
      }
      const updatedData = await response.json()
      // Invalidate cached /me so the next checkAuth() refetches fresh
      // settings instead of seeing the pre-change unit for up to 5 minutes.
      sessionStorage.removeItem('identity_cache')
      notify(translate('pos.profile.glucose_unit_saved'), { type: 'success' })
      return updatedData?.doc ?? null
    } catch {
      // Revert on error so the UI doesn't claim a unit the backend rejected
      setGlucoseUnit(previousUnit)
      notify(translate('pos.profile.glucose_unit_save_failed'), {
        type: 'error',
      })
      return null
    } finally {
      setSaving(false)
    }
  }

  return { glucoseUnit, changeGlucoseUnit, saving }
}
